// JEDNO ŹRÓDŁO stanu utworu (reguła 45, MAPA 32-R5 tabela M2).
//
// STAN UTWORU MÓWI O TEKŚCIE. Artysta ocenia utwór po tym, czy napisy będą dobre;
// reszta — rytm, fala, przeliczenia — jest sprawą serwera i NIE MA własnego stanu,
// akcji ani komunikatu. Dopóki cokolwiek się liczy, utwór jest „analizuję”; gdy tło
// zawiedzie trwale, utwór jest „błąd analizy” z tą samą drogą co nieudana analiza
// tekstu („Ponów analizę tekstu”). Słowo „rytm” nie pada na żadnym ekranie.
//
// TEN PLIK JEST SŁOWNIKIEM CAŁEGO PRODUKTU, nie jednego ekranu. Ten sam stan i to
// samo słowo widać w Muzyce, w oknie „Utwory” Generatora i w Pierwszych krokach —
// wcześniej każde z tych miejsc liczyło stan po swojemu (`Music.tsx#trackState`,
// `Generator.tsx#musicStatusKey`, `OnboardingView.tsx#trackPhase`) i te trzy rachunki
// dawały trzy różne słowa na ten sam utwór.
//
// SZEŚĆ STANÓW, WAGA: danger › info › warn › muted › słowo bez pigułki.

import type { MusicTrack, TrackLyrics } from './types'

export type TrackTextState =
  | 'fileError' | 'analysisError' | 'analyzing' | 'noText' | 'auto' | 'approved'

/** Ton pigułki; `approved` stoi SŁOWEM bez pigułki (stan spokojny, nie nagroda). */
export const TRACK_STATE_TONE: Record<Exclude<TrackTextState, 'approved'>, 'danger' | 'warn' | 'info' | 'muted'> = {
  fileError: 'danger',
  analysisError: 'danger',
  analyzing: 'info',
  noText: 'warn',
  auto: 'muted',
}

/** Klucz słowa w słowniku produktu. Jeden prefiks, sześć wartości. */
export const trackStateKey = (state: TrackTextState) => `appMusic.state.${state}`

/**
 * Stan utworu z odpowiedzi serwera.
 *
 * `lyrics_approved_at` jest polem z paczki MB; starszy serwer go nie przysyła i taki
 * utwór jest „tekst automatyczny” — brak pola to NIE jest zatwierdzenie.
 *
 * `transcript_auto_retry` odróżnia awarię, którą serwer sam jeszcze ponowi, od
 * porażki trwałej: pierwsza jest dalej „analizuję”, bo artysta nie ma w niej nic do
 * zrobienia, i pokazanie mu płatnej drogi naprawy byłoby sprzedaniem czegoś, co
 * serwer zaraz zrobi za darmo.
 */
export function trackState(track: MusicTrack): TrackTextState {
  const beats = track.beats_status ?? (track.beats ? 'done' : 'unknown')
  if (track.transcript === 'failed') {
    if (track.transcript_reason === 'file_invalid') return 'fileError'
    return track.transcript_auto_retry === true ? 'analyzing' : 'analysisError'
  }
  // Trwała porażka tła = ten sam stan i ta sama droga co trwała porażka tekstu.
  if (beats === 'failed') return 'analysisError'
  const done = track.transcript === 'done' || track.transcript === 'corrected'
  // `unknown` i `pending` to dla artysty to samo co `processing`: serwer jeszcze liczy.
  if (!done || beats !== 'done') return 'analyzing'
  if (track.words === 0) return 'noText'
  return track.lyrics_approved_at ? 'approved' : 'auto'
}

/**
 * Czy „Ponów analizę tekstu” ma PRAWO stać przy tym utworze.
 *
 * Warunek jest kopią bramki serwera (`app/assets/service.py#retry_transcription`):
 * przelicza od zera transkrypt NIEUDANY albo brakujący, a gotowy tekst — dopiero gdy
 * cennik zna `music_reanalyze`; nieudane tło przechodzi zawsze. Pozycja, która kończy
 * się odmową serwera, nie ma prawa stać w menu: bez ceny „brak tekstu” dostawał 409
 * i czerwone „Błąd analizy” za każdym razem (przegląd D, M-1).
 *
 * „Analizuję” jest WYŁĄCZONE osobno: dopóki cokolwiek się liczy, serwer odmawia
 * (409 `processing`), a artysta nie ma w tym stanie nic do zrobienia — pokazanie mu
 * tam płatnej drogi to sprzedanie czegoś, co właśnie trwa (M-3).
 */
export function canRetryText(track: MusicTrack, priced: boolean): boolean {
  return canRetryState(trackState(track), priced)
}

/**
 * Ten sam warunek liczony ze STANU, nie z wiersza listy — dla ekranów, które nie mają
 * `MusicTrack`, tylko ładunek tekstu (edytor). Bez tego edytor oferował „Transkrybuj od
 * nowa” bezwarunkowo, dostawał 409 za każdym razem i meldował „Ta analiza już się liczy”
 * o utworze, przy którym nic nie biegło (przegląd E, N5-M1-EDITOR-NOT-GATED).
 */
export function canRetryState(state: TrackTextState, priced: boolean): boolean {
  if (state === 'analyzing') return false
  return priced || state === 'analysisError' || state === 'fileError'
}

/** Warunek dla ŁADUNKU TEKSTU (`GET /api/music/<id>/lyrics`), którym żyje edytor. */
export function canRetryLyrics(payload: LyricsPayload, priced: boolean): boolean {
  return canRetryState(lyricsState(payload), priced)
}

/** „Użyj” jest nieaktywne dokładnie w tych trzech stanach i nigdzie indziej. */
export const trackUsable = (state: TrackTextState) =>
  state === 'noText' || state === 'auto' || state === 'approved'

/** Kropka „wymaga uwagi” pali się WYŁĄCZNIE przy stanie `danger` (reguła 45). */
export const trackNeedsAttention = (state: TrackTextState) =>
  state === 'fileError' || state === 'analysisError'

/**
 * Kolejność listy (M2): `danger` na górze, potem „analizuję”, reszta od najnowszych.
 * Wiersze wgrywane stoją POWYŻEJ listy i nie przechodzą przez ten komparator.
 */
const RANK: Record<TrackTextState, number> = {
  fileError: 0, analysisError: 0, analyzing: 1, noText: 2, auto: 2, approved: 2,
}

export function compareTracks(a: MusicTrack, b: MusicTrack): number {
  const rank = RANK[trackState(a)] - RANK[trackState(b)]
  if (rank !== 0) return rank
  return (b.created_at ?? '').localeCompare(a.created_at ?? '')
}

/**
 * Ten sam słownik dla ŁADUNKU TEKSTU (`GET /api/music/<id>/lyrics`), którym żyje
 * edytor. Ładunek nie niesie stanu tła, więc drabinka jest krótsza o jeden szczebel:
 * w edytorze stan mówi o tym, co edytor edytuje. Słowa są te same, bo to ten sam
 * stan tej samej rzeczy — nagłówek edytora nie ma prawa nazwać utworu inaczej niż
 * wiersz, z którego się do niego weszło.
 */
export type LyricsPayload = Pick<TrackLyrics, 'status' | 'words' | 'reason' | 'auto_retry' | 'lyrics_approved_at'>

export function lyricsState(payload: LyricsPayload): TrackTextState {
  if (payload.status === 'failed') {
    if (payload.reason === 'file_invalid') return 'fileError'
    return payload.auto_retry === true ? 'analyzing' : 'analysisError'
  }
  if (payload.status === 'pending' || payload.status === 'processing') return 'analyzing'
  if (payload.words.length === 0) return 'noText'
  return payload.lyrics_approved_at ? 'approved' : 'auto'
}
